/**
 * CDP SDK Consent Management
 * GDPR-compliant consent tracking and enforcement
 */

import type { ConsentPreferences, ConsentType, EventPayload } from './types';
import { STORAGE_KEYS } from './types';
import { log } from './utils';
import { getStorage, setStorage, getCookie, setCookie } from './storage';

// =============================================================================
// CONSENT STATE
// =============================================================================

// Default consent preferences (opt-out by default for GDPR compliance)
const DEFAULT_CONSENT: ConsentPreferences = {
  functional: true,      // Always enabled
  analytics: false,      // Opt-in required
  marketing: false,      // Opt-in required
  personalization: false, // Opt-in required
};

// Current consent state
let currentConsent: ConsentPreferences = { ...DEFAULT_CONSENT };

// =============================================================================
// MARKETING EVENTS
// =============================================================================

/**
 * Events that require marketing consent
 */
const MARKETING_EVENTS = new Set([
  'Order Completed',
  'Checkout Started',
  'Checkout Completed',
  'Payment Info Entered',
  'Product Added',
  'Product Removed',
  'Cart Viewed',
  'Cart Updated',
  'Coupon Applied',
  'Coupon Removed',
  'Product Added to Wishlist',
  'Product Shared',
]);

/**
 * Events that require personalization consent
 */
const PERSONALIZATION_EVENTS = new Set([
  'identify',
]);

// =============================================================================
// CONSENT OPERATIONS
// =============================================================================

/**
 * Load consent preferences from storage
 */
export function loadConsent(): ConsentPreferences {
  // Try localStorage first
  const stored = getStorage<ConsentPreferences>(STORAGE_KEYS.CONSENT);
  if (stored) {
    currentConsent = { ...DEFAULT_CONSENT, ...stored };
    log('Consent loaded from storage:', currentConsent);
    return currentConsent;
  }

  // Try cookie as fallback
  const cookieValue = getCookie(STORAGE_KEYS.CONSENT);
  if (cookieValue) {
    try {
      const parsed = JSON.parse(cookieValue);
      currentConsent = { ...DEFAULT_CONSENT, ...parsed };
      log('Consent loaded from cookie:', currentConsent);
      return currentConsent;
    } catch {
      // Invalid cookie value
    }
  }

  // Return default
  log('Using default consent:', DEFAULT_CONSENT);
  return DEFAULT_CONSENT;
}

/**
 * Whether this browser has a stored decision at all — a "decline" included.
 * (A stored all-false record is a decision, not the absence of one: without this
 * the visitor who declined got the site's default posture again on every page.)
 */
export function hasStoredConsent(): boolean {
  if (getStorage<ConsentPreferences>(STORAGE_KEYS.CONSENT)) return true;
  return !!getCookie(STORAGE_KEYS.CONSENT);
}

/**
 * Save consent preferences to storage
 */
export function saveConsent(
  preferences: ConsentPreferences,
  cookieDomain?: string
): void {
  currentConsent = { ...DEFAULT_CONSENT, ...preferences };

  // Save to localStorage
  setStorage(STORAGE_KEYS.CONSENT, currentConsent);

  // Also save to cookie for cross-subdomain
  setCookie(
    STORAGE_KEYS.CONSENT,
    JSON.stringify(currentConsent),
    365,
    cookieDomain
  );

  log('Consent saved:', currentConsent);
}

/**
 * Update consent preferences
 */
export function updateConsent(
  preferences: Partial<ConsentPreferences>,
  cookieDomain?: string
): ConsentPreferences {
  const newConsent = { ...currentConsent, ...preferences };
  saveConsent(newConsent, cookieDomain);
  return newConsent;
}

/**
 * Set the in-memory consent state WITHOUT persisting it to cookie/localStorage.
 * Used for the `anonymous-until-consent` lite posture, where analytics is
 * granted only for de-identified measurement and must NOT be written as a stored
 * choice (the visitor hasn't decided yet).
 */
export function setConsentState(preferences: ConsentPreferences): void {
  currentConsent = { ...DEFAULT_CONSENT, ...preferences };
}

/**
 * Get current consent preferences
 */
export function getConsentStatus(): ConsentPreferences {
  return { ...currentConsent };
}

/**
 * Check if a specific consent type is granted
 */
export function hasConsent(type: ConsentType): boolean {
  // Functional is always true
  if (type === 'functional') {
    return true;
  }

  return currentConsent[type] === true;
}

// =============================================================================
// EVENT CONSENT CHECKING
// =============================================================================

/**
 * Determine what consent type is required for an event
 */
export function getRequiredConsentForEvent(
  eventType: string,
  eventName?: string
): ConsentType {
  // Identify events require personalization consent
  if (eventType === 'identify' || PERSONALIZATION_EVENTS.has(eventType)) {
    return 'personalization';
  }

  // Check if event name is a marketing event
  if (eventName && MARKETING_EVENTS.has(eventName)) {
    return 'marketing';
  }

  // Default to analytics consent
  return 'analytics';
}

/**
 * Check if we have consent to send an event
 */
export function canSendEvent(payload: EventPayload): boolean {
  const eventType = payload.type;
  const eventName = payload.type === 'track' ? payload.event : undefined;

  const requiredConsent = getRequiredConsentForEvent(eventType, eventName);

  // Check if we have the required consent
  const allowed = hasConsent(requiredConsent);

  if (!allowed) {
    log(
      'Event blocked - requires',
      requiredConsent,
      'consent:',
      eventType,
      eventName
    );
  }

  return allowed;
}

/**
 * Filter event properties based on consent
 * For example, remove PII if personalization consent is not granted
 */
export function filterEventForConsent(payload: EventPayload): EventPayload {
  // If we don't have personalization consent, remove user-identifying info
  if (!hasConsent('personalization')) {
    const filtered = { ...payload };

    // Remove userId
    filtered.userId = null;

    // Remove traits from identify events
    if (filtered.type === 'identify') {
      filtered.traits = {};
    }

    return filtered;
  }

  return payload;
}

// =============================================================================
// CONSENT RESET
// =============================================================================

/**
 * Reset consent to default (opt-out)
 */
export function resetConsent(cookieDomain?: string): ConsentPreferences {
  currentConsent = { ...DEFAULT_CONSENT };
  saveConsent(currentConsent, cookieDomain);
  log('Consent reset to default');
  return currentConsent;
}

/**
 * Grant all consent types
 */
export function grantAllConsent(cookieDomain?: string): ConsentPreferences {
  const allGranted: ConsentPreferences = {
    functional: true,
    analytics: true,
    marketing: true,
    personalization: true,
  };
  saveConsent(allGranted, cookieDomain);
  log('All consent granted');
  return allGranted;
}

/**
 * Revoke all consent except functional
 */
export function revokeAllConsent(cookieDomain?: string): ConsentPreferences {
  const allRevoked: ConsentPreferences = {
    functional: true,
    analytics: false,
    marketing: false,
    personalization: false,
  };
  saveConsent(allRevoked, cookieDomain);
  log('All consent revoked (except functional)');
  return allRevoked;
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize consent module
 */
export function initConsent(): ConsentPreferences {
  return loadConsent();
}
